/**
 * CrossTabSync.ts - Cross-tab clipboard synchronization
 * 
 * CHANGE HISTORY:
 * - 251102_1225 [pending] danielbeer: Created cross-tab sync using BroadcastChannel
 * 
 * Synchronizes clipboard state across browser tabs using BroadcastChannel API.
 */

import type { ClipboardItem, CrossTabMessage } from '../types';

export interface CrossTabSyncConfig {
  channelName?: string;
  enabled?: boolean;
  syncHistory?: boolean;
  syncSlots?: boolean;
  loggr?: any;
}

/**
 * Cross-tab clipboard synchronization
 * Uses BroadcastChannel API for same-origin tab communication
 */
export class CrossTabSync {
  private channel: BroadcastChannel | null = null;
  private enabled: boolean;
  private syncHistory: boolean;
  private syncSlots: boolean;
  private loggr: any;
  private listeners: Set<(message: CrossTabMessage) => void> = new Set();
  
  constructor(config: CrossTabSyncConfig = {}) {
    this.enabled = config.enabled ?? true;
    this.syncHistory = config.syncHistory ?? true;
    this.syncSlots = config.syncSlots ?? true;
    this.loggr = config.loggr;
    
    if (this.enabled && typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(config.channelName || 'clippr-sync');
        this.setupMessageHandler();
        
        if (this.loggr) {
          this.loggr.info('Cross-tab sync enabled', { 
            channel: config.channelName || 'clippr-sync' 
          });
        }
      } catch (error) {
        if (this.loggr) {
          this.loggr.warn('Failed to create BroadcastChannel', { error });
        }
        this.enabled = false;
      }
    } else {
      this.enabled = false;
      if (this.loggr && config.enabled) {
        this.loggr.warn('BroadcastChannel not supported in this browser');
      }
    }
  }
  
  /**
   * Setup message handler
   */
  private setupMessageHandler(): void {
    if (!this.channel) return;
    
    this.channel.onmessage = (event: MessageEvent<CrossTabMessage>) => {
      const message = event.data;
      
      // Validate message
      if (!this.isValidMessage(message)) {
        if (this.loggr) {
          this.loggr.warn('Invalid cross-tab message received', { message });
        }
        return;
      }
      
      // Notify listeners
      this.listeners.forEach(listener => {
        try {
          listener(message);
        } catch (error) {
          if (this.loggr) {
            this.loggr.error('Cross-tab listener error', { error });
          }
        }
      });
      
      if (this.loggr) {
        this.loggr.debug('Cross-tab message received', { 
          type: message.type,
          slot: message.slot 
        });
      }
    };
  }
  
  /**
   * Validate message structure
   */
  private isValidMessage(message: any): message is CrossTabMessage {
    return (
      message &&
      typeof message === 'object' &&
      typeof message.type === 'string' &&
      typeof message.timestamp === 'number' &&
      ['copy', 'cut', 'paste', 'clear', 'history-sync', 'slot-sync'].includes(message.type)
    );
  }
  
  /**
   * Broadcast copy operation
   */
  broadcastCopy(item: ClipboardItem, slot?: string): void {
    if (!this.enabled || !this.channel) return;
    
    const message: CrossTabMessage = {
      type: 'copy',
      item,
      slot,
      timestamp: Date.now()
    };
    
    this.broadcast(message);
  }
  
  /**
   * Broadcast cut operation
   */
  broadcastCut(item: ClipboardItem, slot?: string): void {
    if (!this.enabled || !this.channel) return;
    
    const message: CrossTabMessage = {
      type: 'cut',
      item,
      slot,
      timestamp: Date.now()
    };
    
    this.broadcast(message);
  }
  
  /**
   * Broadcast paste operation
   */
  broadcastPaste(slot?: string): void {
    if (!this.enabled || !this.channel) return;
    
    const message: CrossTabMessage = {
      type: 'paste',
      slot,
      timestamp: Date.now()
    };
    
    this.broadcast(message);
  }
  
  /**
   * Broadcast clear operation
   */
  broadcastClear(slot?: string): void {
    if (!this.enabled || !this.channel) return;
    
    const message: CrossTabMessage = {
      type: 'clear',
      slot,
      timestamp: Date.now()
    };
    
    this.broadcast(message);
  }
  
  /**
   * Broadcast history sync
   */
  broadcastHistorySync(items: ClipboardItem[]): void {
    if (!this.enabled || !this.channel || !this.syncHistory) return;
    
    const message: CrossTabMessage = {
      type: 'history-sync',
      items,
      timestamp: Date.now()
    };
    
    this.broadcast(message);
  }
  
  /**
   * Broadcast slot sync
   */
  broadcastSlotSync(slotName: string, item: ClipboardItem | null): void {
    if (!this.enabled || !this.channel || !this.syncSlots) return;
    
    const message: CrossTabMessage = {
      type: 'slot-sync',
      slot: slotName,
      item: item || undefined,
      timestamp: Date.now()
    };
    
    this.broadcast(message);
  }
  
  /**
   * Broadcast message
   */
  private broadcast(message: CrossTabMessage): void {
    if (!this.channel) return;
    
    try {
      this.channel.postMessage(message);
      
      if (this.loggr) {
        this.loggr.debug('Cross-tab message broadcast', { 
          type: message.type,
          slot: message.slot 
        });
      }
    } catch (error) {
      if (this.loggr) {
        this.loggr.error('Failed to broadcast message', { error });
      }
    }
  }
  
  /**
   * Subscribe to cross-tab messages
   */
  subscribe(listener: (message: CrossTabMessage) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  /**
   * Check if sync is enabled
   */
  isEnabled(): boolean {
    return this.enabled && this.channel !== null;
  }
  
  /**
   * Close channel and cleanup
   */
  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
      this.listeners.clear();
      
      if (this.loggr) {
        this.loggr.info('Cross-tab sync closed');
      }
    }
  }
}
